import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { CoordinateGridOptions, CoordinateQuestionOptions } from "../Constants.ts";
import { labelFor, withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The two coordinate-plane dials (ADR 0085 §2, §4): what to ask, and which
 * grid. Both basic tier, as the format's own panel is (ADR 0023 §2).
 *
 * A quadrant question needs four quadrants, so the two are kept consistent
 * here rather than reinterpreted later: picking Quadrant moves the grid to
 * four quadrants, and picking the first quadrant while Quadrant is selected
 * moves the question to Mixed (ADR 0085 §4).
 */
export function CoordinateSettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const t = useTranslation();

    function handleQuestionChange(value: CoordinateQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked again.
        if (!value) {
            return;
        }
        const grid =
            value === CoordinateQuestionOptions.Quadrant ? CoordinateGridOptions.FourQuadrants : settings.CoordinateGrid;
        setSettings({ ...settings, CoordinateQuestion: value, CoordinateGrid: grid });
    }

    function handleGridChange(event) {
        const grid = event.target.value as CoordinateGridOptions;
        const question =
            grid === CoordinateGridOptions.FirstQuadrant && settings.CoordinateQuestion === CoordinateQuestionOptions.Quadrant
                ? CoordinateQuestionOptions.Mixed
                : settings.CoordinateQuestion;
        setSettings({ ...settings, CoordinateGrid: grid, CoordinateQuestion: question });
    }

    return (
        <div className="coordinateSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.CoordinateQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(CoordinateQuestionOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.coordinateGridLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.coordinateGridAria')}>
                {Object.values(CoordinateGridOptions).map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`coordinateGrid-${option}`}
                            value={option}
                            checked={settings.CoordinateGrid === option}
                            onChange={handleGridChange}
                        />
                        <label htmlFor={`coordinateGrid-${option}`}>{labelFor(option, settings.Language)}</label>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default CoordinateSettings;
